<template>
    <div
        class="sidebar"
        :data-background-color="backgroundColor"
        :data-active-color="activeColor"
    >
        <div class="sidebar-wrapper" id="style-3" ref="wrapperRef">
            <div class="logo">
                <a :href="logoHref" class="simple-text" :title="title">
                    <div class="logo-img">
                        <img :src="logo" alt="" />
                    </div>
                    <span>{{ title }}</span>
                </a>
            </div>
            <slot></slot>
            <ul class="nav" ref="navRef">
                <slot name="links">
                    <template
                        v-for="(item, index) in sidebarLinks"
                        :key="index"
                    >
                        <sidebar-group
                            v-if="item.children && item.children.length"
                            :key="`group-${index}`"
                            :name="item.name"
                            :icon="item.icon"
                            :children="item.children"
                            :base-path="item.path"
                        />
                        <sidebar-link
                            v-else
                            :to="item.path"
                            :name="item.name"
                            :icon="item.icon"
                            :key="`link-${index}`"
                        />
                    </template>
                </slot>
            </ul>
            <moving-arrow ref="arrowRef" :move-y="arrowMovePx" />
        </div>
    </div>
</template>

<script>
import {
    ref,
    onMounted,
    watch,
    provide,
    nextTick,
    onBeforeUnmount,
    computed,
} from 'vue';
import { useRoute } from 'vue-router';
import MovingArrow from './MovingArrow.vue';
import SidebarGroup from './SidebarGroup.vue';

export default {
    name: 'SidebarComponent',
    components: { MovingArrow, SidebarGroup },
    props: {
        logo: {
            type: String,
            default: `${process.env.BASE_URL}assets/img/dhc-logo.png`,
        },
        title: { type: String, default: 'Club Assist SaaS' },
        backgroundColor: {
            type: String,
            default: 'black',
            validator: v => ['white', 'black', 'darkblue'].includes(v),
        },
        activeColor: {
            type: String,
            default: 'success',
            validator: v =>
                ['primary', 'info', 'success', 'warning', 'danger'].includes(v),
        },
        sidebarLinks: { type: Array, default: () => [] },
        autoClose: { type: Boolean, default: true },
    },
    setup(props) {
        const route = useRoute();
        const navRef = ref(null);
        const wrapperRef = ref(null);
        const arrowMovePx = ref(0);
        const arrowRef = ref(null);
        const moRef = ref(null);
        const logoHref = computed(() => {
            const first = props.sidebarLinks?.[0];
            if (!first) return '/';
            return first.path || '/';
        });

        const accumulateOffset = (el, stopEl) => {
            let sum = 0;
            while (el && el !== stopEl && el instanceof HTMLElement) {
                sum += el.offsetTop;
                el = el.offsetParent;
            }
            return sum;
        };

        const getDepth = (el, stopEl) => {
            let d = 0;
            while (el && el !== stopEl) {
                if (el.tagName === 'UL') d++;
                el = el.parentElement;
            }
            return d;
        };

        const findActiveLi = nav => {
            // 1) 以 href 與 route.fullPath 精準比對（含 query）
            const links = Array.from(nav.querySelectorAll('a.nav-link[href]'));
            const exact = links.find(
                a => a.getAttribute('href') === route.fullPath
            );
            if (exact) return exact.closest('li.nav-item');

            // 2) 若父層與子層都有 active，取「最深層」的那個
            const candidates = Array.from(
                nav.querySelectorAll('li.nav-item.active')
            );
            if (candidates.length) {
                return candidates.sort(
                    (a, b) => getDepth(b, nav) - getDepth(a, nav)
                )[0];
            }

            // 3) 回退 router-link-exact-active
            const exactActive = nav.querySelector('a.router-link-exact-active');
            return exactActive ? exactActive.closest('li.nav-item') : null;
        };

        const recalcArrow = () => {
            nextTick(() => {
                const nav = navRef.value;
                const wrapper = wrapperRef.value;
                if (!nav || !wrapper) return;

                const activeLi = findActiveLi(nav);
                if (!activeLi) {
                    arrowMovePx.value = 0;
                    return;
                }

                const targetEl =
                    activeLi.querySelector('a.nav-link') || activeLi;

                // 在 wrapper「內容座標」計算中心點（不要扣 scrollTop）
                const y =
                    accumulateOffset(targetEl, wrapper) +
                    targetEl.offsetHeight / 2;

                arrowMovePx.value = Math.round(y);
            });
        };

        provide('autoClose', props.autoClose);
        provide('recalcArrow', recalcArrow);

        watch(
            () => route.fullPath,
            () => recalcArrow(),
            { immediate: true }
        );
        watch(
            () => props.sidebarLinks,
            () => recalcArrow(),
            { deep: true, immediate: true }
        );

        const onResize = () => recalcArrow();
        const onScroll = () => recalcArrow();

        onMounted(() => {
            recalcArrow();
            window.addEventListener('resize', onResize);
            wrapperRef.value?.addEventListener('scroll', onScroll, {
                passive: true,
            });

            // 群組展開/收合或動態渲染時，重新計算箭頭
            if (navRef.value) {
                moRef.value = new MutationObserver(() => recalcArrow());
                moRef.value.observe(navRef.value, {
                    attributes: true,
                    childList: true,
                    subtree: true,
                });
            }
        });

        onBeforeUnmount(() => {
            window.removeEventListener('resize', onResize);
            wrapperRef.value?.removeEventListener('scroll', onScroll);
            moRef.value?.disconnect();
        });

        return { navRef, wrapperRef, arrowRef, arrowMovePx, logoHref };
    },
};
</script>

<style scoped>
/* 確保箭頭用 wrapper 當定位基準 */
.sidebar-wrapper {
    position: relative;
    /* 若需要可滾動 */
    overflow-y: auto;
}
</style>
